// Constants
import { TaskGroupingOption, UnassignedTaskGroup } from "@ms/uno-constants/lib/local/GroupingConstants";
// Utilities
import cloneDeep from "lodash/cloneDeep";
import { normalizeOrderHint } from "../../../utilities/OrderHintUtilties";
/**
 * Implementation of ITaskPlanViewOrderHints
 */ export class TaskPlanViewOrderHints {
    /**
     * Creates an instance of TaskPlanViewOrderHints from legacy data.
     * @param orderHint The order hint from legacy data.
     * @param bucketOrderHint The bucket order hint from legacy data.
     * @param statusOrderHint The status order hint from legacy data.
     * @param unassignedOrderHint The unassigned order hint from legacy data.
     * @param assigneeOrderHints The assignee order hints from legacy data.
     */ static fromLegacyData(orderHint, bucketOrderHint, statusOrderHint, unassignedOrderHint, assigneeOrderHints) {
        const groupedOrderHints = {};
        const assigneeHints = {
            unassignedOrderHint: unassignedOrderHint,
            assigneeOrderHints: {}
        };
        // Add bucket order hint
        groupedOrderHints[TaskGroupingOption.Bucket] = bucketOrderHint;
        // Add status order hint
        groupedOrderHints[TaskGroupingOption.Status] = statusOrderHint;
        // Add unassigned order hint
        assigneeHints.unassignedOrderHint = unassignedOrderHint;
        // Add assignee order hints (safely handle null/undefined)
        assigneeHints.assigneeOrderHints = assigneeOrderHints;
        return new TaskPlanViewOrderHints(orderHint, assigneeHints, groupedOrderHints);
    }
    /**
     * Creates an instance from the first-class `Task.PlanView.FormatOrderHints` shape.
     *
     * Only the three board-format scalars were promoted service-side; `orderHint` and the
     * per-assignee map still arrive via the LegacyData property bag, so they are passed in
     * separately.
     * @param orderHint The flat list/grid order hint, from legacy data
     * @param formatOrderHints The first-class board format order hints, if the service sent them
     * @param assigneeOrderHints The per-assignee order hints, from legacy data
     */ static fromFormatOrderHints(orderHint, formatOrderHints, assigneeOrderHints) {
        const groupedOrderHints = {
            [TaskGroupingOption.Bucket]: formatOrderHints?.bucketTaskBoardFormatOrderHint ?? "",
            [TaskGroupingOption.Status]: formatOrderHints?.progressTaskBoardFormatOrderHint ?? ""
        };
        const assigneeHints = {
            unassignedOrderHint: formatOrderHints?.assignedToTaskBoardFormatUnassignedOrderHint ?? "",
            assigneeOrderHints: assigneeOrderHints
        };
        return new TaskPlanViewOrderHints(orderHint, assigneeHints, groupedOrderHints);
    }
    get(grouping, key = "") {
        switch(grouping){
            case TaskGroupingOption.None:
                return this.orderHint;
            case TaskGroupingOption.Assignee:
                if (key === UnassignedTaskGroup) {
                    return this.assigneeOrderHints.unassignedOrderHint;
                }
                return this.assigneeOrderHints.assigneeOrderHints[key] ?? null;
            default:
                // Other groupings are based on their singular value and thus key is not required
                return this.groupedOrderHints[`${grouping}`];
        }
    }
    set(grouping, value, key = "") {
        let orderHint = this.orderHint;
        const assigneeOrderHints = cloneDeep(this.assigneeOrderHints);
        const groupedOrderHints = cloneDeep(this.groupedOrderHints);
        switch(grouping){
            case TaskGroupingOption.None:
                orderHint = value;
                break;
            case TaskGroupingOption.Assignee:
                if (key === UnassignedTaskGroup) {
                    assigneeOrderHints.unassignedOrderHint = value;
                } else {
                    assigneeOrderHints.assigneeOrderHints[key] = value;
                }
                break;
            case TaskGroupingOption.Bucket:
                groupedOrderHints[TaskGroupingOption.Bucket] = value;
                break;
            case TaskGroupingOption.Status:
                groupedOrderHints[TaskGroupingOption.Status] = value;
                break;
            default:
                break;
        }
        return new TaskPlanViewOrderHints(orderHint, assigneeOrderHints, groupedOrderHints);
    }
    forSerialization() {
        return {
            orderHint: this.orderHint,
            assignedToTaskBoardFormatUnassignedOrderHint: this.assigneeOrderHints.unassignedOrderHint,
            assignedToTaskBoardFormatAssigneeOrderHints: this.assigneeOrderHints.assigneeOrderHints,
            bucketTaskBoardFormatOrderHint: this.groupedOrderHints[TaskGroupingOption.Bucket],
            progressTaskBoardFormatOrderHint: this.groupedOrderHints[TaskGroupingOption.Status]
        };
    }
    /**
     * Serializes only the hints that exist as first-class properties under
     * `Task.PlanView.FormatOrderHints`. The flat `orderHint` and the per-assignee map are
     * deliberately excluded — they have no first-class counterpart and stay in LegacyData.
     */ forFormatOrderHintsSerialization() {
        return {
            assignedToTaskBoardFormatUnassignedOrderHint: this.assigneeOrderHints.unassignedOrderHint,
            bucketTaskBoardFormatOrderHint: this.groupedOrderHints[TaskGroupingOption.Bucket],
            progressTaskBoardFormatOrderHint: this.groupedOrderHints[TaskGroupingOption.Status]
        };
    }
    constructor(orderHint, assigneeOrderHints, groupedOrderHints){
        this.orderHint = normalizeOrderHint(orderHint);
        this.assigneeOrderHints = {
            unassignedOrderHint: "",
            assigneeOrderHints: {}
        };
        this.assigneeOrderHints.unassignedOrderHint = normalizeOrderHint(assigneeOrderHints?.unassignedOrderHint);
        for (const [key, value] of Object.entries(assigneeOrderHints?.assigneeOrderHints || {})){
            this.assigneeOrderHints.assigneeOrderHints[key] = normalizeOrderHint(value);
        }
        this.groupedOrderHints = {
            [TaskGroupingOption.Bucket]: normalizeOrderHint(groupedOrderHints?.[TaskGroupingOption.Bucket]),
            [TaskGroupingOption.Status]: normalizeOrderHint(groupedOrderHints?.[TaskGroupingOption.Status])
        };
    }
}
